import type { RawImagePresignedUrlItem } from "api/model";
import { getRawImagePresignedUrls } from "api/orthos/orthos";
import { getApiErrorMessage } from "utils";

const DOWNLOAD_CONCURRENCY = 4;

const getRawImageUrlsError = getApiErrorMessage("Failed to download images.");

export interface RawImageDownloadTarget {
  /** Calendar day, `YYYY-MM-DD`. */
  date: string;
  siteName: string;
  /** Original upload folder (`user_folder`). Omitted when the flight has none. */
  userFolder?: string | null;
}

export interface DownloadRawImagesOptions {
  /** Task `description` keyed by `file_hash`. S3 object names are the hash. */
  descriptionsByHash?: ReadonlyMap<string, string>;
  onProgress?: (completed: number, total: number) => void;
}

export type DownloadRawImagesResult =
  | { kind: "cancelled" }
  | { kind: "error"; message: string }
  | { kind: "saved"; fileCount: number };

type ShowDirectoryPicker = (options?: {
  mode?: "read" | "readwrite";
}) => Promise<FileSystemDirectoryHandle>;

const readShowDirectoryPicker = (): ShowDirectoryPicker | null => {
  const windowWithPicker = window as unknown as {
    showDirectoryPicker?: ShowDirectoryPicker;
  };
  return windowWithPicker.showDirectoryPicker ?? null;
};

const isAbortError = (error: unknown): boolean =>
  (error instanceof DOMException || error instanceof Error) &&
  error.name === "AbortError";

/** Drops characters that are illegal in a file or folder name. */
export const sanitizePathSegment = (
  value: string,
  fallback: string,
): string => {
  const sanitized = value.replaceAll(/[\\/:*?"<>|]/g, "-").trim();
  if (sanitized === "" || sanitized === "." || sanitized === "..") {
    return fallback;
  }
  return sanitized;
};

export const lastPathSegment = (path: string): string =>
  path.replaceAll("\\", "/").split("/").filter(Boolean).pop() ?? "";

/** Original file name from the task description, or the S3 object name (the file hash). */
export const fileNameForRawImage = (
  s3Key: string,
  descriptionsByHash?: ReadonlyMap<string, string>,
): string => {
  const objectName = lastPathSegment(s3Key);
  const hash = objectName.replace(/\.jpe?g$/i, "").toLowerCase();
  const description = descriptionsByHash?.get(hash);
  const hashFileName = `${hash}.jpg`;
  if (description == null) {
    return hashFileName;
  }
  return sanitizePathSegment(lastPathSegment(description), hashFileName);
};

export const uniqueFileName = (
  usedNames: Set<string>,
  fileName: string,
): string => {
  if (!usedNames.has(fileName)) {
    usedNames.add(fileName);
    return fileName;
  }

  const dotIndex = fileName.lastIndexOf(".");
  const hasExtension = dotIndex > 0;
  const stem = hasExtension ? fileName.slice(0, dotIndex) : fileName;
  const extension = hasExtension ? fileName.slice(dotIndex) : "";
  let suffix = 2;
  let candidate = `${stem}-${suffix}${extension}`;
  while (usedNames.has(candidate)) {
    suffix += 1;
    candidate = `${stem}-${suffix}${extension}`;
  }
  usedNames.add(candidate);
  return candidate;
};

interface PlannedImageFile {
  fileName: string;
  presignedUrl: string;
}

/**
 * Avoid duplicate file names, just in case
 */
export const planImageFiles = (
  items: readonly RawImagePresignedUrlItem[],
  descriptionsByHash?: ReadonlyMap<string, string>,
): PlannedImageFile[] => {
  const usedNames = new Set<string>();
  return items.map((item) => ({
    fileName: uniqueFileName(
      usedNames,
      fileNameForRawImage(item.s3_key, descriptionsByHash),
    ),
    presignedUrl: item.presigned_url,
  }));
};

const triggerBrowserDownload = (blob: Blob, fileName: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 1000);
};

/**
 * Browsers without the directory picker (Firefox) save each file through the
 * normal download flow, which uses the default downloads folder.
 */
const downloadImagesToDownloadsFolder = async (
  files: readonly PlannedImageFile[],
  onProgress?: (completed: number, total: number) => void,
): Promise<Error | null> => {
  onProgress?.(0, files.length);

  for (const [index, file] of files.entries()) {
    try {
      const response = await fetch(file.presignedUrl);
      if (!response.ok) {
        return new Error(`Failed to download ${file.fileName}.`);
      }
      triggerBrowserDownload(await response.blob(), file.fileName);
    } catch {
      return new Error(`Failed to download ${file.fileName}.`);
    }
    onProgress?.(index + 1, files.length);
  }

  return null;
};

const openNestedDirectory = async (
  root: FileSystemDirectoryHandle,
  segments: readonly string[],
): Promise<FileSystemDirectoryHandle> => {
  let current = root;
  for (const segment of segments) {
    current = await current.getDirectoryHandle(
      sanitizePathSegment(segment, "unknown"),
      { create: true },
    );
  }
  return current;
};

const savePresignedImage = async (
  directory: FileSystemDirectoryHandle,
  fileName: string,
  presignedUrl: string,
): Promise<void> => {
  let response: Response;
  try {
    response = await fetch(presignedUrl);
  } catch {
    throw new Error(`Failed to download ${fileName}.`);
  }
  if (!response.ok || response.body == null) {
    throw new Error(`Failed to download ${fileName}.`);
  }

  const fileHandle = await directory.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await response.body.pipeTo(writable);
};

const saveImages = async (
  directory: FileSystemDirectoryHandle,
  files: readonly PlannedImageFile[],
  onProgress?: (completed: number, total: number) => void,
): Promise<Error | null> => {
  let nextIndex = 0;
  let completed = 0;
  let failure: Error | null = null;
  onProgress?.(0, files.length);

  const runWorker = async () => {
    while (failure == null && nextIndex < files.length) {
      const index = nextIndex;
      nextIndex += 1;
      const file = files[index];
      if (file == null) {
        return;
      }
      try {
        await savePresignedImage(directory, file.fileName, file.presignedUrl);
      } catch (error) {
        failure ??=
          error instanceof Error
            ? error
            : new Error("Failed to download images.");
        return;
      }
      completed += 1;
      onProgress?.(completed, files.length);
    }
  };

  const workerCount = Math.min(DOWNLOAD_CONCURRENCY, files.length);
  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
  return failure;
};

/**
 * Writes `{siteName}/{date}/{userFolder}/*.jpg` from `GET /orthos/raw/urls`.
 * `userFolder` is left out when the flight has no `user_folder`.
 * When the browser cannot create folders, each image is saved to the default
 * downloads folder instead.
 */
export const downloadOrthoRawImages = async (
  orthoId: number,
  target: RawImageDownloadTarget,
  options?: DownloadRawImagesOptions,
): Promise<DownloadRawImagesResult> => {
  const showDirectoryPicker = readShowDirectoryPicker();
  let root: FileSystemDirectoryHandle | null = null;
  if (showDirectoryPicker != null) {
    try {
      root = await showDirectoryPicker({ mode: "readwrite" });
    } catch (error) {
      if (isAbortError(error)) {
        return { kind: "cancelled" };
      }
      return {
        kind: "error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to choose a download folder.",
      };
    }
  }

  const { descriptionsByHash, onProgress } = options ?? {};
  onProgress?.(0, 0);

  let response: Awaited<ReturnType<typeof getRawImagePresignedUrls>>;
  try {
    response = await getRawImagePresignedUrls({ ortho_id: orthoId });
  } catch {
    return { kind: "error", message: "Failed to download images." };
  }
  if (response.status !== 200) {
    return { kind: "error", message: getRawImageUrlsError(response.data) };
  }

  const items = response.data;
  if (items.length === 0) {
    return {
      kind: "error",
      message: "No images were found for this flight.",
    };
  }

  const files = planImageFiles(items, descriptionsByHash);

  if (root == null) {
    const saveError = await downloadImagesToDownloadsFolder(files, onProgress);
    if (saveError != null) {
      return { kind: "error", message: saveError.message };
    }
    return { kind: "saved", fileCount: items.length };
  }

  try {
    const userFolder = target.userFolder?.trim() ?? "";
    const flightDirectory = await openNestedDirectory(root, [
      target.siteName,
      target.date,
      ...(userFolder !== "" ? [userFolder] : []),
    ]);
    const saveError = await saveImages(flightDirectory, files, onProgress);
    if (saveError != null) {
      return { kind: "error", message: saveError.message };
    }
  } catch (error) {
    return {
      kind: "error",
      message:
        error instanceof Error ? error.message : "Failed to download images.",
    };
  }

  return { kind: "saved", fileCount: items.length };
};
